<template>
  <div class="container-header">
    <nav class="container-header-content">
      <div class="left">
        <img :src="getAssetImageUrl('common/logos/logo-sconchat-full.png')" alt="Logo" class="logo" aria-label="Go home" @click="goHome" />
        <p class="medium id" v-if="teamId">
          <span class="dim">{{ t('organization_id') }}</span
          >{{ teamId }}
        </p>
      </div>

      <div class="right">
        <!--크레딧및플랜-->
        <ButtonCreditInfo v-if="isUsingCredit" :using-auto-update="isUsingCredit" />

        <!--언어-->
        <button class="button-white s-btn button-rounded global-btn hide-mobile" @click="toggleModalLanguage">
          <img :src="getAssetImageUrl('common/icons/global.svg')" alt="" />
          <span class="accent">{{ t('language_meta') }}</span>
        </button>

        <!-- 모바일 메뉴 -->
        <button type="button" class="mob-menu-btn" @click="openMenu">
          <IconHamburger></IconHamburger>
        </button>
      </div>

      <NavMenu
        v-if="isVisibleDropdownCallIncoming"
        :targetEl="refButtonCallIncoming"
        :content-class="['onhold-position']"
        :show="true"
        :title="t('label_on_hold_interpretation_list')"
        @close="closeDropdownCallIncoming"
      >
        <div class="refresh-modal" @click="getCallRequestOnHold">
          <RefreshIcon />
        </div>
        <ul>
          <li v-for="call in callIncomingOnHold" :key="call.chatId" class="waiting-call-item">
            <div class="call-info">
              <p class="call-title">{{ call.title }}</p>
              <p class="call-waiting">
                {{ formatWaitingTime(call.waitingSeconds) }}
              </p>
            </div>
            <button class="call-icon" @click="callTake(call)">
              <IconPhone style="width: 24px; height: 24px" />
            </button>
          </li>
          <li v-if="callIncomingOnHold.length === 0">
            <div class="no-call">
              <p class="small">{{ t('alert_no_on_hold_interpretations') }}</p>
            </div>
          </li>
        </ul>
      </NavMenu>

      <ModalRequestCallIncoming v-if="isVisibleModalCallIncoming" :call-incoming @take="callTake" @hold="callHold" />
      <ModalDownloadApp v-if="isVisibleModalAppDownload" @close="closeModalAppDownload" />

      <Transition name="slide-menu">
        <DashboardMobileMenu v-if="isMenuOpen" @close="closeMenu" />
      </Transition>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import { fetchWaitingCallList, holdConsultationCall } from '@/api/apiService';
import ModalRequestCallIncoming from '@/components/domains/admin/common/ModalRequestCallIncoming.vue';
import ButtonCreditInfo from '@/components/domains/chat/common/ButtonCreditInfo.vue';
import DashboardMobileMenu from '@/components/domains/self-service/dashboard/DashboardMobileMenu.vue';
import IconHamburger from '@/components/icons/IconHamburger.vue';
import IconPhone from '@/components/icons/IconPhone.vue';
import RefreshIcon from '@/components/icons/IconRefresh.vue';
import ModalDownloadApp from '@/components/shared/composite/ModalDownloadApp.vue';
import NavMenu from '@/components/shared/composite/NavMenu.vue';
import { useUserStore } from '@/stores/user';
import { IResponseConsultationRecent } from '@/types/response';
import { ij_toggleModalLanguage } from '@/utils/injectionKeys';

withDefaults(
  defineProps<{
    domain?: 'mobile' | 'self-service';
    isUsingCredit?: boolean;
  }>(),
  {
    domain: 'mobile',
    isUsingCredit: false,
  }
);

const { t } = useI18n();
const router = useRouter();

const userStore = useUserStore();

const toggleModalLanguage = inject(ij_toggleModalLanguage, () => {});

const isMenuOpen = ref(false);
const isVisibleModalCallIncoming = ref<boolean>(false); // 수신된 통화 요청 모달 표시 여부
const isVisibleModalAppDownload = ref<boolean>(false); // 앱 다운로드 모달 표시 여부
const isVisibleDropdownCallIncoming = ref<boolean>(false); // 수신된 통화 요청 드롭다운 표시 여부
const refButtonCallIncoming = ref<HTMLElement>();
const callIncoming = ref(); // 수신된 통화 요청
const callIncomingOnHold = ref<IResponseConsultationRecent[]>([]); // 보류된 통화 요청

let intervalCallRequest = 0;

const teamId = computed<string>(() => {
  const { teamId = '' } = userStore.userInfo || {};
  return teamId;
});

// 모바일 메뉴
const handleMenuBack = () => {
  history.pushState(history.state, '', location.href);
  closeMenu();
};

const openMenu = () => {
  isMenuOpen.value = true;
  history.pushState(history.state, '', location.href);
  window.addEventListener('popstate', handleMenuBack);
  const el = document.querySelector('.container-selfservice-content');
  if (el) el.style.overflowY = 'hidden';
};

const closeMenu = () => {
  isMenuOpen.value = false;
  window.removeEventListener('popstate', handleMenuBack);
  const el = document.querySelector('.container-selfservice-content');
  if (el) el.style.overflowY = '';
};

const goHome = () => {
  router.push({ name: 'ss-dashboard' });
};

// 어플 다운로드 모달 닫기
const closeModalAppDownload = () => {
  isVisibleModalAppDownload.value = false;
};

// 수신된 통화 요청 모달 열기
const closeModalCallIncoming = () => {
  isVisibleModalCallIncoming.value = false;
};

// 보류된 통화 요청 드롭다운 토글
const closeDropdownCallIncoming = () => {
  isVisibleDropdownCallIncoming.value = false;
};

// 통화 보류
const callHold = async () => {
  await holdConsultationCall(callIncoming.value.chatId);
  closeModalCallIncoming();
  getCallRequestOnHold();
};

// 통화 보류
const callTake = async (call?: IResponseConsultationRecent) => {
  const { code } = call ? call : callIncoming.value;
  const newUrl = router.resolve({
    name: 'chat-entrance',
    params: { code },
  }).href;
  window.open(newUrl, '_blank');
  closeModalCallIncoming();
  closeDropdownCallIncoming();
};

// 보류된 통화 요청 가져오기
const getCallRequestOnHold = async () => {
  const { data: resCallOnHold } = await fetchWaitingCallList();
  callIncomingOnHold.value = resCallOnHold ?? [];
};

// 보류중인 통화 요청 대기 시간 포맷팅
const formatWaitingTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}${t('label_minute')} ${remainingSeconds}${t('label_seconds')} ${t('label_waiting')}`;
};

onMounted(() => {
  history.pushState(history.state, '', location.href);
});

onUnmounted(() => {
  clearInterval(intervalCallRequest);
  window.removeEventListener('popstate', handleMenuBack);
});
</script>

<style scoped lang="scss">
.icon-waiting-call {
  fill: var(--font-default);
  --circle-color: var(--font-default);
}

.container-header-content {
  .left {
    display: flex;
    height: 3rem;
    width: 100%;
    gap: 0.5rem;
    align-items: center;
    margin-right: 0.5rem;
    cursor: pointer;
    min-width: 0;

    @include respond-to(mobile-landscape) {
      .id {
        display: none;
      }
      justify-content: space-between;
    }

    .logo {
      height: 2.25rem;
      display: none;

      @include respond-to(mobile-landscape) {
        display: block;
        position: relative;
        height: 30px;
      }
    }

    p {
      display: block;
      min-width: 0;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      span {
        margin-right: 0.5rem;
      }
    }
  }

  .right {
    display: flex;
    width: 100%;
    justify-content: flex-end;
    align-items: center;
    gap: 0;
    height: 3rem;
    gap: 1.5rem;

    @include respond-to(mobile-landscape) {
      gap: 0.5rem;
    }

    .global-btn {
      gap: 4px;
    }

    .button-white {
      &:hover :deep(.icon-dim) {
        fill: var(--primary) !important;
      }
    }

    .mob-menu-btn {
      display: none;
      padding: 2px;
      border-radius: $size-xs;
      svg {
        width: 22px;
        height: 22px;
      }
    }

    .icon-waiting-call {
      width: 2rem;
      height: 2rem;

      &.new {
        fill: var(--font-primary);
        --circle-color: #18c142;

        :deep(.circle) {
          animation: circle-blink 1s steps(1, start) infinite;
        }
      }
    }

    @keyframes circle-blink {
      0%,
      49% {
        opacity: 1;
      }
      50%,
      100% {
        opacity: 0;
      }
    }
  }
}

.button-credit {
  display: flex;

  .credit-left {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    box-shadow:
      0 -1px 0 var(--border-dim),
      -1px 0 0 var(--border-dim),
      0 1px 0 var(--border-dim);
    padding-right: 0.25rem;
  }
  .credit-right {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    box-shadow:
      0 -1px 0 var(--border-dim),
      1px 0 0 var(--border-dim),
      0 1px 0 var(--border-dim);
    padding-left: 0.25rem;
  }
}

.wrapper-appbuttons {
  position: sticky;
  top: 4.5rem;
  margin: 0.5rem auto 0 auto;
  z-index: $z-header;
}

.waiting-call-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  cursor: pointer;
}

.call-info {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.call-waiting {
  color: var(--font-dim-dark);
}

.call-icon {
  margin-left: 16px;
  display: flex;
  width: 2rem;
  height: 2rem;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background: var(--success);
  flex: none;
}

.refresh-modal {
  position: absolute;
  top: 1rem;
  right: 0.75rem;
  cursor: pointer;
}

:deep(.profile-position) {
  min-width: 5rem;
  padding: 0.5rem;
}

@include respond-to(mobile-landscape) {
  .container-header-content {
    .right {
      .mob-menu-btn {
        display: block;
      }
    }
  }
}

.slide-menu-enter-active,
.slide-menu-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-transition: -webkit-transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}
.slide-menu-enter-from,
.slide-menu-leave-to {
  transform: translateX(100%);
  -webkit-transform: translateX(100%);
}
.slide-menu-enter-to,
.slide-menu-leave-from {
  transform: translateX(0);
  -webkit-transform: translateX(0);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
